import {AGENCY_KEYS, useAgencyQuery, useAgencySubbrandsQuery} from "@/api/agencyApi";
import DotIcon from "@/assets/colorable/dot.svg?react";
import EditIcon from "@/assets/colorable/edit.svg?react";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import TickIcon from "@/assets/colorable/tick_medium.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import UsersIcon from "@/assets/colorable/users.svg?react";
import CompanyLogoPlaceholder from "@/assets/images/company_logo_placeholder.svg";
import UserAvatarPlaceholder from "@/assets/images/user_avatar_placeholder.svg";
import BankCardIcon from "@/assets/light_gradient/bank_card.svg?react";
import CircleWithQuestionMarkIcon from "@/assets/light_gradient/circle_with_question_mark.svg?react";
import PlusIcon from "@/assets/light_gradient/plus.svg?react";
import SignOutIcon from "@/assets/light_gradient/sign_out.svg?react";
import UserIcon from "@/assets/light_gradient/user.svg?react";
import SidebarProfileTrigger from "@/components/NavBar/SidebarProfileTrigger";
import type {SubBrandListSerializer} from "@/generated/DjangoTypes";
import {useLogout} from "@/hooks/useLogout";
import {useHelpUrl} from "@/hooks/useNavigationHooks";
import {packages} from "@/hooks/usePackages";
import {
  useAgencyId,
  useAgencySeats,
  useSelectedAgency,
  useSelectedCompanyReactive,
  useUserPermission,
} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {queryClient} from "@/queryClient";
import {getMissingProfileFields} from "@/store/company";
import {AGENCY_ROUTES_WITHOUT_NAVIGATION, INFINITY_THRESHOLD, PERMISSION_LABEL_KEYS} from "@/store/constants";
import {
  createCustomerModalOpenAtom,
  isSidebarProfileMenuOpenAtom,
  selectedCompanyIdAtom,
  settingsModalAtom,
} from "@/store/jotaiAtoms";
import type {TSettingsModalState} from "@/store/types";
import useUserStore from "@/store/user";
import {Link, useLocation, useNavigate} from "@tanstack/react-router";
import type {AxiosResponse} from "axios";
import {produce, WritableDraft} from "immer";
import {useSetAtom} from "jotai";
import {useEffect, useRef, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useClickAway, useHoverDirty, useMedia, useUpdateEffect} from "react-use";

const NavbarAgencyProfile: FC<{isCollapsed: boolean}> = ({isCollapsed}) => {
  const {t} = useTranslation(["navigation", "common", "multi_account"]);
  const location = useLocation();
  const navigate = useNavigate();
  const helpUrl = useHelpUrl();
  const screenHeight = useMedia("(min-height: 868px)");
  const addCustomer = useSetAtom(createCustomerModalOpenAtom);
  const setSelectedCompanyId = useSetAtom(selectedCompanyIdAtom);
  const setSettingsModal = useSetAtom(settingsModalAtom);
  const setIsProfileMenuOpen = useSetAtom(isSidebarProfileMenuOpenAtom);
  const user = useUserStore(state => state.user);
  const userPermission = useUserPermission();
  const logout = useLogout();
  const seats = useAgencySeats();
  const agencyId = useAgencyId();
  const agency = useSelectedAgency();
  const selectedCompany = useSelectedCompanyReactive();
  const subscritpion = useUserStore(state => state.user.package_subscription);
  const pkg = packages[subscritpion?.name!];
  const {data: subBrands} = useAgencySubbrandsQuery({
    agencyId: Number(agencyId),
  });
  const isAgencyScope = !selectedCompany?.id;
  const {data: agencyDetail} = useAgencyQuery({
    agencyId: Number(agencyId),
    queryOptions: {enabled: !!agencyId && isAgencyScope},
  });
  const isAgencyOwner = agencyDetail?.agency_owner_id === Number(user.id);
  const openSettings = (settings: TSettingsModalState) => {
    setIsDropdownOpen(false);
    setSettingsModal(settings);
  };
  const openUserProfile = () => openSettings({scope: {id: agencyId!, type: "agency"}, section: "account_profile"});

  const hasHover = useMedia("(hover: hover)");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null!);

  useClickAway(dropdownRef, () => setIsDropdownOpen(false));
  useUpdateEffect(() => setIsDropdownOpen(false), [location.pathname]);

  const isHovered = useHoverDirty(dropdownRef);
  const isOpen = hasHover ? isHovered : isDropdownOpen;

  useEffect(() => {
    setIsProfileMenuOpen(isOpen);
    return () => setIsProfileMenuOpen(false);
  }, [isOpen, setIsProfileMenuOpen]);

  return (
    <div ref={dropdownRef} className="group relative">
      <SidebarProfileTrigger
        imageUrl={(!selectedCompany?.id ? agency?.picture : selectedCompany?.picture320) ?? CompanyLogoPlaceholder}
        isCollapsed={isCollapsed}
        isOpen={isOpen}
        name={selectedCompany?.company_name ?? agency?.name}
        onClick={hasHover ? undefined : () => setIsDropdownOpen(!isDropdownOpen)}
      />
      <div
        aria-hidden
        className={clsxm(
          "bg-neutral-black/40 fixed inset-0 transition-opacity duration-200 sm:hidden",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={() => setIsDropdownOpen(false)}
      />
      <div
        className={clsxm(
          "absolute bottom-0 left-0 origin-bottom-left transition-all duration-200 sm:left-full sm:pb-2 sm:pl-2 md:bottom-(--cookie-banner-height)",
          hasHover
            ? "scale-0 opacity-0 delay-200 group-hover:scale-100 group-hover:opacity-100 group-hover:delay-0"
            : isDropdownOpen
              ? "scale-100 opacity-100"
              : "pointer-events-none scale-0 opacity-0",
        )}>
        <div className="divide-neutral-grey-lightest border-neutral-white-dark max-h-[calc(100dvh-var(--cookie-banner-height)-1rem)] min-w-64 flex-col divide-y overflow-hidden rounded-md border bg-white drop-shadow-lg *:px-3 *:py-4 2xl:h-fit">
          <div className="flex flex-col gap-2 [&]:py-2">
            <div className="flex flex-row items-center justify-between gap-2">
              <div className="flex items-center gap-1">
                <div className="border-neutral-grey-light-dark size-6 shrink-0 rounded-full border">
                  <img src={user.profile_picture ?? UserAvatarPlaceholder} className="h-full w-full rounded-full" />
                </div>
                <span className="bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent">
                  {user.first_name}
                </span>
              </div>
              <span className="text-neutral-black text-xs text-nowrap">
                {isAgencyScope
                  ? t(isAgencyOwner ? "multi_account:admin_permissions" : "common:admin")
                  : t(PERMISSION_LABEL_KEYS[userPermission])}
              </span>
            </div>
            <button onClick={openUserProfile} className="text-primary-dark-solid self-start text-xs hover:font-bold">
              {t("navigation:edit_profile")}
            </button>
          </div>
          <div className="text-neutral-grey-dark text-center text-xs [&]:py-1">
            {t(agency?.large_brand ? "multi_account:large_brand" : "multi_account:agency")}
          </div>
          <div
            className={clsxm(
              "flex flex-row items-center justify-between",
              !selectedCompany?.id && "bg-primary-powder",
            )}>
            <div className="flex flex-row items-center gap-2">
              <img
                src={agency?.picture320 ?? CompanyLogoPlaceholder}
                className={clsxm(
                  "border-neutral-grey-lightest size-10 self-start rounded-full border",
                  !screenHeight && "size-8",
                )}
              />
              <div className="flex flex-col gap-1">
                <span className="font-medium">{agency?.name}</span>
                {pkg && (
                  <div className="flex flex-row items-center gap-1">
                    <pkg.borderedIcon className="size-3" />
                    <span className="bg-linear-(--primary-dark-gradient) bg-clip-text text-xs text-transparent">
                      {t(`common:${pkg.translationKey as "packageAdvanced"}`)}
                    </span>
                    <DotIcon className="text-neutral-grey-dark size-1" />
                    {subscritpion?.instagram_campaigns && <InstagramIcon className="text-neutral-grey-dark size-3" />}
                    {subscritpion?.tiktok_campaigns && <TiktokIcon className="text-neutral-grey-dark size-3" />}
                  </div>
                )}
                <div className={clsxm("mt-1 flex flex-col gap-2", screenHeight ? "flex-col gap-2" : "flex-row gap-4")}>
                  <button
                    onClick={() => openSettings({scope: {id: agencyId!, type: "agency"}, section: "users"})}
                    className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal transition-all hover:font-semibold max-sm:min-h-5 max-sm:py-1">
                    <UsersIcon className="size-3" />
                    {screenHeight && t("users")}
                  </button>
                  <button
                    onClick={() => openSettings({scope: {id: agencyId!, type: "agency"}, section: "info"})}
                    className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal no-underline transition-all hover:font-semibold max-sm:min-h-5 max-sm:py-1">
                    <EditIcon className="size-3" />
                    {screenHeight && t("common:Edit")}
                  </button>
                </div>
              </div>
            </div>
            {!selectedCompany?.id ? (
              <TickIcon className="text-primary-medium-solid self-center" />
            ) : (
              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  if (AGENCY_ROUTES_WITHOUT_NAVIGATION.includes(location.pathname)) {
                    setSelectedCompanyId(null);
                  } else {
                    navigate({to: "/agency/$agencyId/dashboard", params: {agencyId: agencyId!}});
                  }
                }}
                className="flex items-center justify-center self-center max-sm:size-11">
                <RightArrowIcon className="border-primary-light-solid text-neutral-grey-light hover:text-primary-dark-solid hover:border-primary-dark-solid size-8 shrink-0 rounded-sm border p-2.5 transition-all duration-150" />
              </button>
            )}
          </div>
          <div className="text-neutral-grey-dark text-center text-xs [&]:py-1">
            {agency?.large_brand ? t("navigation:companies") : t("navigation:customers")}
          </div>
          <div className="divide-neutral-grey-lightest flex max-h-[calc(100vh-590px)] flex-col divide-y overflow-auto 2xl:max-h-[calc(100vh-670px)] [&]:px-0 [&]:py-0">
            {subBrands?.map(brand => (
              <div
                key={brand.id}
                className={clsxm(
                  "flex shrink flex-row justify-between gap-3 px-3 py-4",
                  selectedCompany?.id === brand.id && "bg-primary-powder",
                )}>
                <div className="flex flex-row items-center gap-3">
                  <img
                    src={brand.picture320 ?? CompanyLogoPlaceholder}
                    className={clsxm(
                      "border-neutral-grey-lightest size-10 self-start rounded-full border",
                      !screenHeight && "size-8",
                    )}
                  />
                  <div className={clsxm("flex flex-col", screenHeight ? "gap-3" : "gap-1")}>
                    <span className="font-medium">{brand.company_name}</span>
                    <div className={clsxm("mt-1 flex gap-2", !screenHeight ? "flex-row gap-4" : "flex-col")}>
                      <button
                        onClick={() => openSettings({scope: {id: brand.id, type: "brand"}, section: "users"})}
                        className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal transition-all hover:font-semibold max-sm:min-h-5 max-sm:py-1">
                        <UsersIcon className="size-3" />
                        {screenHeight && t("users")}
                      </button>
                      <button
                        onClick={() => openSettings({scope: {id: brand.id!, type: "brand"}, section: "info"})}
                        className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal no-underline transition-all hover:font-semibold max-sm:min-h-5 max-sm:py-1">
                        <EditIcon className="size-3" />
                        {screenHeight && t("navigation:edit_customer")}
                        {getMissingProfileFields(brand).length > 0 && (
                          <>
                            <span
                              aria-hidden
                              className="bg-warning-mustard ml-auto size-2 shrink-0 self-center rounded-full border border-white"
                            />
                            <span className="sr-only">{t("navigation:profile_incomplete")}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                {selectedCompany?.id === brand.id ? (
                  <TickIcon className="text-primary-medium-solid self-center" />
                ) : (
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      queryClient.setQueryData<WritableDraft<AxiosResponse<SubBrandListSerializer[]>>>(
                        AGENCY_KEYS.qAgencySubbrands(agencyId!),
                        prevState =>
                          produce(prevState, draft => {
                            if (!draft?.data) return;

                            const idx = draft.data.findIndex(b => b.id === brand.id);

                            if (idx === -1) return;

                            const [itemToMove] = draft.data.splice(idx, 1);
                            draft.data.unshift(itemToMove);
                          }),
                      );

                      if (AGENCY_ROUTES_WITHOUT_NAVIGATION.includes(location.pathname)) {
                        setSelectedCompanyId(brand.id);
                      } else {
                        navigate({
                          to: "/agency/$agencyId/subbrand/$companyId/dashboard",
                          params: {agencyId: agencyId!, companyId: brand.id!},
                        });
                      }
                    }}
                    className="flex items-center justify-center self-center max-sm:size-11">
                    <RightArrowIcon className="border-primary-light-solid text-neutral-grey-light hover:text-primary-dark-solid hover:border-primary-dark-solid size-8 shrink-0 rounded-sm border p-2.5 transition-all duration-150" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="flex flex-row justify-between text-xs">
            <button
              className={clsxm("flex flex-row items-center gap-2", !seats && "cursor-not-allowed")}
              disabled={!seats}
              onClick={() => {
                setIsDropdownOpen(false);
                addCustomer(true);
              }}>
              <PlusIcon className="size-3.5" />
              <span>{t(agency?.large_brand ? "multi_account:add_company" : "multi_account:add_customer")}</span>
            </button>
            {seats < INFINITY_THRESHOLD && (
              <div className="rounded-full bg-linear-(--primary-light-gradient) p-0.5">
                <div className="h-full w-full rounded-full bg-white px-2 py-1 text-xs font-semibold">
                  <span className="bg-linear-(--primary-light-gradient) bg-clip-text font-bold text-transparent">
                    {t("multi_account:seats_left_short", {count: seats || 0})}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="text-neutral-grey-dark text-center text-xs [&]:py-1">{t("navigation:settings")}</div>
          <div
            className="hover:bg-neutral-white-dark border-neutral-grey-lightest flex h-auto cursor-pointer gap-2 border-b text-xs font-normal"
            onClick={openUserProfile}>
            <UserIcon className="h-4 w-auto shrink-0" />
            {t("navigation:your_profile")}
          </div>
          <Can I="read" a="PaymentHistory" passThrough>
            {allowed =>
              allowed &&
              selectedCompany?.id &&
              agencyId && (
                <Link
                  className="hover:bg-neutral-white-dark border-neutral-grey-lightest flex h-auto cursor-pointer gap-2 border-b px-2.5 py-3 text-xs font-normal no-underline"
                  to="/agency/$agencyId/subbrand/$companyId/invoices"
                  params={{agencyId: agencyId, companyId: selectedCompany.id}}>
                  <BankCardIcon className="h-4 w-auto shrink-0" />
                  {t("payment_history")}
                </Link>
              )
            }
          </Can>
          <a
            className="hover:bg-neutral-white-dark border-neutral-grey-lightest flex cursor-pointer gap-2 border-b no-underline"
            href={helpUrl}>
            <CircleWithQuestionMarkIcon className="h-4 w-auto shrink-0" />
            <span className="text-xs font-normal">{t("navigation:help")}</span>
          </a>
          <div onClick={logout} className="hover:bg-neutral-white-dark flex cursor-pointer gap-2">
            <SignOutIcon className="h-4 w-auto shrink-0" />
            <span className="text-xs font-normal">{t("navigation:logout")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavbarAgencyProfile;
